<template>
  <div class="pigeondoc">
    <div class="pigeondoc__toolbar">
      <SaveToPdf class="pigeondoc__toolbar__pdf-save" v-if="pdfDownload" :document="document" />

      <VersionManager
        :versions="versions"
        @undo="undo"
        @redo="redo"
        @save="delayedSave"
        :saveError="saveError"
      />
    </div>

    <div class="pigeondoc__content">
      <div ref="document" class="pigeondoc__nodes">
        <NodeManager
          v-for="(node, index) in document.nodes"
          :key="node.uuid"
          v-model="document.nodes[index]"
          @input="delayedSave"
          :display="index === editingIndex ? 'edit' : 'view'"
          :editable="editable"
          :thisCollection="id"
          :isICCS="isICCS"
          @nodeMoveStart="nodeMoveStart"
          ref="nodes"
          @nodeAdded="nodeAdded"
        />
      </div>
    </div>

    <!-- <AddNode @addNode="addNode" /> -->
  </div>
</template>

<script>
// Components
// import AddNode from "../AddNode/AddNode"
import NodeManager from '../NodeManager/NodeManager';
import SaveToPdf from '../SaveToPdf/SaveToPdf';
import VersionManager from '../VersionManager/VersionManager.vue';
import Versions from '../../lib/versions';

//import TableOfContents from "../TableOfContents/TableOfContents"

// Models
import PigeonDocModel from '../../models/PigeonDocModel';

import nodeMoveHandler from './nodeMoveHandler';

export default {
  name: 'PigeonDoc',
  props: {
    id: {
      type: String,
      default: () => '',
    },
    savedHandler: {
      type: Function,
      default: () => {},
    },
    Model: {
      type: Function,
      default: PigeonDocModel,
    },
    value: {
      type: Object,
      default: () => {},
    },
    pdfDownload: {
      type: Boolean,
      default: () => true,
    },
    editable: {
      type: Boolean,
      default: () => false,
    },
    isICCS: {
      type: Boolean,
      default: () => false,
    },
  },
  components: {
    NodeManager,
    SaveToPdf,
    VersionManager,
    //TableOfContents
  },
  async mounted() {
    this.load();
  },
  watch: {
    id() {
      this.load();
    },
    value: {
      handler() {
        this.load();
      },
      deep: true,
    },
  },
  data() {
    return {
      document: new this.Model({
        name: 'New PigeonDoc',
      }),
      editingIndex: null,
      domNodes: [],
      versions: new Versions(),
      saveError: false,
    };
  },
  methods: {
    ...nodeMoveHandler,
    async load() {
      if (this.value && this.id) {
        throw new Error(
          'PigeonDoc component has been passed a doc instance and an ID. Please only use one method.',
        );
      }
      if (this.value) {
        this.document = this.value;
        this.$emit('loaded', this.document);
        this.scrollToAnchor();
      } else if (this.id) {
        const project = await this.$pigeonline.projects.getByID(this.Model, this.id);
        this.document = project;
        this.$emit('loaded', this.document);
        this.scrollToAnchor();
      }

      this.versions.add(this.document.nodes);

      // Override default SVG <a> behavior to avoid Vue.js page reloading
      this.$nextTick(() => {
        const component = this;
        const svgAs = document.querySelectorAll('svg a');
        svgAs.forEach((a) => {
          a.addEventListener('click', function (e) {
            e.preventDefault();
            history.pushState(null, null, this.href.baseVal);
            component.scrollToAnchor();
          });
        });
      });

      // this.$refs.docHeading.innerHTML = this.document.name

      this.$emit('updateSlides');
    },
    addNode(node) {
      this.document.nodes.push(node);
      this.editingIndex = this.document.nodes.length - 1;
      window.scrollTo(0, document.body.scrollHeight + 200);
    },
    nodeAdded(node) {
      this.domNodes.push(node);
    },
    headingUpdated(evt) {
      this.document.name = evt.target.innerText;
      this.delayedSave(evt);
    },
    delayedSave() {
      this.saveError = false;
      clearTimeout(this.timer);
      this.versions.saving();

      this.timer = setTimeout(async () => {
        if (this.document.id) {
          this.versions.add(this.document.nodes);
          const project = await this.$pigeonline.projects.update(this.document);
          this.document = project;
          this.versions.saved(
            new Date(this.document.last_modified_on.$date).toLocaleTimeString([], {
              hour12: true,
              hour: 'numeric',
              minute: 'numeric',
            }),
          );
        } else {
          try {
            const project = await this.$pigeonline.projects.create(this.document);
            this.document = project;

            if (this.savedHandler) {
              this.savedHandler(this.document);
            }

            this.$emit('saved', this.document);
            this.versions.saved(
              new Date(this.document.last_modified_on.$date).toLocaleTimeString([], {
                hour12: true,
                hour: 'numeric',
                minute: 'numeric',
              }),
            );
          } catch (e) {
            this.saveError = true;
            //console.error(e);
          }
        }
      }, 2000);
    },
    scrollToAnchor() {
      if (window.location.hash) {
        this.$nextTick(() => {
          const el = document.getElementById(window.location.hash.replace('#', ''));
          if (el) el.scrollIntoView();
        });
      }
    },
    savedNotification() {
      this.$refs['saved-notification'].style.display = 'inline';
      setTimeout(() => {
        this.$refs['saved-notification'].classList.add(
          'version-toolbar__saved-notification--fade-out',
        );
      }, 1000);

      this.$refs['saved-notification'].addEventListener('transitionend', () => {
        this.$refs['saved-notification'].style.display = 'none';
        this.$refs['saved-notification'].classList.remove(
          'version-toolbar__saved-notification--fade-out',
        );
      });
    },
    undo(current) {
      this.document['nodes'] = current;
    },
    redo(current) {
      this.document['nodes'] = current;
    },
  },
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Open+Sans&family=Open+Sans+Condensed:wght@700&display=swap');

.pigeondoc {
  max-width: 100ch;
  margin: auto;
  font-family: 'Open Sans', sans-serif;
  font-size: 18px;
  line-height: 2;
  text-align: left;
}

.pigeondoc__toolbar {
  display: flex;
  padding-bottom: 0.5rem;
}

.pigeondoc__content {
  max-height: 95vh;
  overflow: auto;
  padding-bottom: 4rem;
  box-sizing: border-box;
}

.pigeondoc__toolbar__pdf-save {
  margin-right: auto;
}

.pigeondoc__heading {
  margin-top: 4rem;
  margin-bottom: 1rem;
}

.pigeondoc__nodes {
  display: flex;
  flex-wrap: wrap;
}

.pigeondoc_section {
  display: flex;
  flex-wrap: wrap;
}

.document-node {
  position: relative;
  flex-basis: 100%;
  max-width: 100%;
  padding-left: 4ch;
}

.document-node--width-50 {
  flex-basis: calc(50% - 8ch);
}

.document-node--width-50 + .document-node--width-50 {
  margin-left: 1rem;
}

.document-node__edit {
  position: absolute;
  top: 0;
  right: 0ch;
}

.document-node__active {
  border: 1px solid rgba(21, 156, 228, 0.4);
  box-shadow: 0 0 0 2px rgba(21, 156, 228, 0.2);
}

.document-node__close-edit {
  position: absolute;
  top: 0;
  right: 0ch;
}

.document-node .document-node__edit,
.document-node .document-node__edit,
.document-node .document-node__layout-buttons {
  display: none;
}

.document-node:hover .document-node__edit,
.document-node:hover .document-node__edit,
.document-node:hover .document-node__layout-buttons {
  display: block;
}

.document-node__layout-buttons {
  position: absolute;
  top: 0;
  left: 0;
  line-height: 0;
}

.document-node__layout-buttons__disclose:hover + .document-node__layout-buttons__items {
  display: block;
}
.document-node__layout-buttons__items:hover {
  display: block;
}
.document-node__layout-buttons__items {
  display: none;
  background: white;
  padding: 0.5rem;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.3);
  border-radius: 5px;
}

.document-node__alt-table {
  width: 100%;
}

.document-node:hover .reorder-btn {
  visibility: visible;
}

.reorder-btn {
  position: absolute;
  top: 0;
  left: 0px;
  visibility: hidden;
  cursor: pointer;
}

.pigeondoc-chart {
  font-size: 16px;
}

.chart__axis-label {
  font-family: 'Open Sans Condensed', sans-serif;
}

.version-toolbar {
  position: fixed;
  bottom: 0;
  right: 0;
  display: flex;
  justify-content: flex-end;
}

.version-toolbar__saved-notification {
  display: none;
  background-color: green;
  color: white;
  opacity: 1;
  transition: opacity 2s ease-in-out;
  padding: 5px 10px;
}

.version-toolbar__saved-notification--fade-out {
  opacity: 0;
}

.version-toolbar__undo-button {
  cursor: pointer;
}
</style>
